import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Check, X, ArrowLeft, Star, Plane, CalendarDays, Loader2 } from "lucide-react";
import { formatINR } from "../mock";
import api, { imgUrl } from "../lib/api";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import BookingModal from "../components/BookingModal";

const statusStyles = {
  "AVAILABLE": "bg-emerald-50 text-emerald-700",
  "ALMOST SOLD OUT": "bg-orange-50 text-orange-700",
  "SOLD OUT": "bg-red-50 text-red-600",
};

export default function TourDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/tours/${slug}`)
      .then((res) => setTour(res.data))
      .catch(() => setTour(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral-600" />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-center">
          <p className="text-slate-500">Tour not found.</p>
          <Link to="/tours" className="text-coral-600 font-semibold">Back to tours</Link>
        </div>
      </div>
    );
  }

  const soldOut = tour.status === "SOLD OUT";

  return (
    <div className="bg-white min-h-screen">
      {/* Gallery hero */}
      <section className="pt-16">
        <div className="max-w-7xl mx-auto px-5 pt-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-coral-600 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-2xl overflow-hidden h-[360px]">
              <img src={imgUrl(tour.gallery[active])} alt={tour.title} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
              {tour.gallery.slice(0, 3).map((g, i) => (
                <button key={i} onClick={() => setActive(i)}
                  className={`rounded-xl overflow-hidden h-[112px] border-2 ${active === i ? "border-coral-600" : "border-transparent"}`}>
                  <img src={imgUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-5 py-10 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={`text-[11px] font-bold px-2 py-1 rounded ${statusStyles[tour.status]}`}>{tour.status}</span>
            {tour.withFlights && <span className="text-[11px] font-semibold text-coral-700 bg-coral-50 px-2 py-1 rounded flex items-center gap-1"><Plane className="w-3 h-3" /> With Flights</span>}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{tour.title}</h1>
          <p className="mt-2 text-coral-600 font-medium">{tour.tagline}</p>
          <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-coral-600" /> {tour.location}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-coral-600" /> {tour.duration}</span>
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> 4.9 (120+ reviews)</span>
          </div>
          <p className="mt-6 text-slate-600 leading-relaxed">{tour.description}</p>

          <h2 className="mt-10 text-xl font-bold text-slate-900">Highlights</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {tour.highlights.map((h) => (
              <div key={h} className="flex items-center gap-2 bg-slate-50 rounded-lg px-4 py-3">
                <Check className="w-4 h-4 text-emerald-600" /> <span className="text-sm text-slate-700">{h}</span>
              </div>
            ))}
          </div>

          <h2 className="mt-10 text-xl font-bold text-slate-900">Itinerary</h2>
          <div className="mt-4 space-y-4">
            {tour.itinerary.map((d) => (
              <div key={d.day} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-coral-600 text-white text-sm font-bold shrink-0">{d.day}</span>
                  {d.day < tour.itinerary.length && <span className="flex-1 w-px bg-slate-200 my-1" />}
                </div>
                <div className="pb-2">
                  <h4 className="font-semibold text-slate-900">{d.title}</h4>
                  <p className="text-sm text-slate-500">{d.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            <div>
              <h3 className="font-bold text-slate-900 mb-3">Inclusions</h3>
              <ul className="space-y-2">
                {tour.inclusions.map((i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-600" /> {i}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-3">Exclusions</h3>
              <ul className="space-y-2">
                {tour.exclusions.map((i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-600"><X className="w-4 h-4 text-red-500" /> {i}</li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* Booking card */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 shadow-lg p-6">
            <p className="text-sm text-slate-500">Starting from</p>
            <p className="font-display text-3xl font-bold text-slate-900">{formatINR(tour.price)} <span className="text-sm font-normal text-slate-500">/ person</span></p>
            <p className="mt-1 text-xs text-slate-400">+ 2% TCS extra (as applicable)</p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-coral-600" /> {tour.duration}</div>
              <div className="flex items-center gap-2"><Plane className="w-4 h-4 text-coral-600" /> Flights &amp; transfers included</div>
            </div>
            <Button disabled={soldOut} onClick={() => setBookOpen(true)}
              className="mt-5 w-full h-12 bg-coral-600 hover:bg-coral-700 text-white rounded-xl disabled:opacity-50">
              {soldOut ? "Sold Out" : "Book This Trip"}
            </Button>
            <p className="mt-3 text-center text-xs text-slate-400">Secure your seat • Pay later via Razorpay</p>
          </div>
        </div>
      </section>

      <BookingModal open={bookOpen} onOpenChange={setBookOpen} tour={tour} />
      <Footer />
    </div>
  );
}
